/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import appStylesHref from '@/theme/index.css?url';
import criticalCss from '@/theme/critical.generated.css?inline';

/**
 * Critical CSS loading contract
 *
 * This component intentionally separates fetching index.css from applying it. The preload starts
 * the index.css request at high priority, while the inline critical CSS lets the browser paint the
 * app shell without waiting for the complete stylesheet. This can materially improve FCP and LCP
 * on slow mobile networks because index.css is no longer a render-blocking dependency.
 *
 * The ordering is essential. React 19 may hoist a preload link to the beginning of the rendered
 * <head>, regardless of its JSX position. We therefore must not turn the preload itself into a
 * stylesheet: doing so would apply index.css before the critical <style>, allowing later critical
 * utility rules to override complete responsive rules with equal specificity.
 *
 * Instead, a second link owns stylesheet application. It uses the same URL as the preload so the
 * browser can reuse the request, and media="print" keeps it out of the initial render-blocking path.
 * This applying link must remain after style[data-critical-css] in the rendered DOM. The inline
 * script changes only its media to "all" after loading; because the node does not move, index.css
 * stays later in the cascade and correctly wins equal-specificity conflicts. The cached-sheet check
 * and window-load fallback cover timing races, and activation is idempotent.
 */
export const APP_STYLESHEET_ACTIVATION_SCRIPT =
    "(function(){var l=document.getElementById('app-stylesheet');if(!l)return;var d=false;function a(){if(d)return;d=true;l.media='all'}l.addEventListener('load',a,{once:true});if(l.sheet)a();else addEventListener('load',a,{once:true})})()";

export function CriticalCss({ nonce }: { nonce?: string }) {
    return (
        <>
            {/* Critical styles must precede the applying stylesheet so the complete CSS wins the cascade. */}
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: build-time-generated first-party CSS */}
            {/* oxlint-disable-next-line react/no-danger -- build-time-generated first-party CSS */}
            <style data-critical-css="" dangerouslySetInnerHTML={{ __html: criticalCss }} />
            {/* React may hoist preload resources to the start of <head>; this link never applies styles. */}
            <link rel="preload" as="style" href={appStylesHref} id="app-stylesheet-preload" />
            {/* The preload warms the same URL while print media keeps this stylesheet non-render-blocking. */}
            <link rel="stylesheet" media="print" href={appStylesHref} id="app-stylesheet" suppressHydrationWarning />
            <script
                nonce={nonce}
                suppressHydrationWarning
                // biome-ignore lint/security/noDangerouslySetInnerHtml: static first-party stylesheet activation
                // oxlint-disable-next-line react/no-danger -- static first-party stylesheet activation
                dangerouslySetInnerHTML={{ __html: APP_STYLESHEET_ACTIVATION_SCRIPT }}
            />
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: raw <link> so React does not hoist it out of <noscript> */}
            {/* oxlint-disable-next-line react/no-danger -- raw link keeps React from hoisting it out of noscript */}
            <noscript dangerouslySetInnerHTML={{ __html: `<link rel="stylesheet" href="${appStylesHref}">` }} />
        </>
    );
}
